import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { useReactTable, getCoreRowModel, createColumnHelper } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import StatCard from "./ReportsCards";
import { getAuthCookie } from "../utils/authCookies";

type CardsSummary = {
  total_orders: number;       // here = total removals
  total_revenue: number;      // sum(total_amount)
  amount_paid: number;        // sum(paid)
  outstanding_balance: number;// sum(balance)
};

interface RemovalSummaryRow {
  brokerage_firm: string;
  office: string;
  office_admin: string;
  agent_name: string;
  removals: number;
  total_amount: number;
  paid: number;
  balance: number;
  over_paid: boolean;
  order_number: string;
  removal_date: string;
  status: string;
  notes: string;
  amount: number;
  payment_status: string;
  payment_method: string;
}

const DATE_OPTIONS = [
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 15 Days", value: "last_15_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Custom", value: "custom" },
];

const PAYMENT_STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Paid", value: "Paid" },
  { label: "Partial Payment", value: "Partial Payment" },
  { label: "Pending", value: "Pending" },
  { label: "Overdue", value: "Overdue" },
];

const PAYMENT_METHOD_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Card", value: "Card" },
  { label: "Net 30", value: "Net 30" },
  { label: "Bank Transfer", value: "Bank Transfer" },
];

type SelectOption = { label: string; value: string };

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
  multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
  // Fix dropdown menu positioning to appear below the input
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: "200px",
  }),
};

export default function RemovalSummaryReport() {
  const token = getAuthCookie("sessionToken") || "";

  // table + data
  const [data, setData] = useState<RemovalSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // filters
  const [datePreset, setDatePreset] = useState<SelectOption>(DATE_OPTIONS[2]); // Last 30 Days
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<SelectOption>(PAYMENT_STATUS_OPTIONS[0]); // All
  const [paymentMethod, setPaymentMethod] = useState<SelectOption>(PAYMENT_METHOD_OPTIONS[0]); // All
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");     // applied value
  const [searchInput, setSearchInput] = useState<string>(""); // input box
  const [exporting, setExporting] = useState(false);
  
  // Calculate date range limits (last 6 months from today)
  const dateRangeLimits = useMemo(() => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    
    // Format as YYYY-MM-DD for date input
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      minDate: formatDate(sixMonthsAgo),
      maxDate: formatDate(today)
    };
  }, []);

  // Validate and reset dates when custom is selected or dates are out of range
  useEffect(() => {
    if (datePreset.value === "custom") {
      const minDate = dateRangeLimits.minDate;
      const maxDate = dateRangeLimits.maxDate;
      
      // Reset From date if it's outside the valid range
      if (customFrom && (customFrom < minDate || customFrom > maxDate)) {
        setCustomFrom("");
      }
      
      // Reset To date if it's outside the valid range
      if (customTo && (customTo < minDate || customTo > maxDate)) {
        setCustomTo("");
      }
    }
  }, [datePreset.value, dateRangeLimits, customFrom, customTo]);

  // cards summary (computed)
  const [cards, setCards] = useState<CardsSummary>({
    total_orders: 0,
    total_revenue: 0,
    amount_paid: 0,
    outstanding_balance: 0,
  });

  // Generate button flow (same pattern as your other page)
  const [generateClicked, setGenerateClicked] = useState(false);
  function handleGenerate() {
    setGlobalFilter(searchInput.trim());
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setGenerateClicked((v) => !v);
  }

  // Function to clear all filters
  function handleClearFilters() {
    setDatePreset(DATE_OPTIONS[2]); // Last 30 Days
    setCustomFrom("");
    setCustomTo("");
    setPaymentStatus(PAYMENT_STATUS_OPTIONS[0]); // All
    setPaymentMethod(PAYMENT_METHOD_OPTIONS[0]); // All
    setGlobalFilter("");
    setSearchInput("");
    setPagination({ pageIndex: 0, pageSize: 10 });
    setGenerateClicked((v) => !v);
  }

  // fetch
  async function fetchData() {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/reports/signage/removal_summary/datatable`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            payment_status: paymentStatus.value === "all" ? [] : [paymentStatus.value],
            // payment_method: paymentMethod.value === "all" ? "" : paymentMethod.value,
            search: globalFilter, // backend currently accepts this single search field
            date_range: {
              type: datePreset.value,
              from: datePreset.value === "custom" ? customFrom : "",
              to: datePreset.value === "custom" ? customTo : "",
            },
          }),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        toast.error(result?.error || "Failed to fetch removal summary.");
        setData([]);
        setRowCount(0);
        setCards({ total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 });
        return;
      }

      const rows: RemovalSummaryRow[] = Array.isArray(result?.data) ? result.data : [];
      setData(rows);
      setRowCount(Number(result?.total || rows.length || 0));

      // compute cards from rows (robust even if API doesn't send cards)
      const totals = rows.reduce(
        (acc, r) => {
          acc.total_orders += Number(r.removals || 0);
          acc.total_revenue += Number(r.total_amount || 0);
          acc.amount_paid += Number(r.paid || 0);
          acc.outstanding_balance += Number(r.balance || 0);
          return acc;
        },
        { total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 }
      );
      setCards(totals);
    } catch (e) {
      toast.error("Network error while fetching removal summary.");
      setData([]);
      setRowCount(0);
      setCards({ total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 });
    } finally {
      setLoading(false);
    }
  }

  const handleExportCsv = async () => {
    if (!token) return;
    try {
      setExporting(true);
      const payload = {
        page: 1,
        limit: rowCount || 10000,
        search: globalFilter ?? "",
        payment_status: paymentStatus.value === "all" ? [] : [paymentStatus.value],
        date_range: {
          type: datePreset.value,
          from: datePreset.value === "custom" ? customFrom : "",
          to: datePreset.value === "custom" ? customTo : "",
        },
      };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/signage/removal_summary/export`, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        console.error("Download failed:", response.statusText);
        toast.error("Export failed");
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.headers.get("Content-Disposition")?.split("filename=")[1]?.replace(/"/g, "") || "signage_removal_summary.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err: any) {
      console.error("Export error:", err);
      toast.error(err?.message || "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  // load on first render + when Generate is clicked + when paging changes
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [generateClicked, pagination.pageIndex, pagination.pageSize]);

  // columns
  const columnHelper = createColumnHelper<RemovalSummaryRow>();
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      // columnHelper.accessor("order_number", {
      //   header: () => <div className="th-text">Order ID</div>,
      //   cell: (info) => (
      //     <div className="fw-medium text-sm">
      //       {info.getValue() ? `#${info.getValue()}` : "--"}
      //     </div>
      //   ),
      // }),
      
      // columnHelper.display({
      //   id: "ordered_by",
      //   header: () => <div className="th-text">Ordered By</div>,
      //   cell: (info) => (
      //     <div className="text-sm">
      //       {info.row.original.agent_name || info.row.original.office_admin || "--"}
      //     </div>
      //   ),
      // }),
      columnHelper.accessor("brokerage_firm", {
        header: () => <div className="th-text">Brokerage Firm</div>,
        cell: (info) => <div className="fw-medium text-sm">{info.getValue() || "--"}</div>,
      }),
      columnHelper.accessor("office", {
        header: () => <div className="th-text">Office</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
      }),
      columnHelper.accessor("office_admin", {
        header: () => <div className="th-text">Office Admin</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
      }),
      // columnHelper.accessor("agent_name", {
      //   header: () => <div className="th-text">Agent Name</div>,
      //   cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
      // }),
      columnHelper.accessor("agent_name", {
        header: () => <div className="th-text">Agent Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
      }),
      columnHelper.accessor("removals", {
        header: () => <div className="th-text ">Removals</div>,
        cell: (info) => (
            <div className="text-sm">{info.getValue() ?? 0}</div>
        ),
    }),
      // columnHelper.accessor("removal_date", {
      //   header: () => <div className="th-text">Removal Date</div>,
      //   cell: (info) => {
      //     const date = info.getValue();
      //     if (!date || date === "0001-01-01T00:00:00Z") return <div className="text-sm">--</div>;
      //     try {
      //       const formattedDate = new Date(date).toLocaleDateString("en-US", {
      //         year: "numeric",
      //         month: "long",
      //         day: "numeric",
      //       });
      //       return <div className="text-sm">{formattedDate}</div>;
      //     } catch {
      //       return <div className="text-sm">--</div>;
      //     }
      //   },
      // }),
      // columnHelper.accessor("status", {
      //   header: () => <div className="th-text">Status</div>,
      //   cell: (info) => {
      //     const status = info.getValue();
      //     return (
      //       <div className="text-sm">
      //         {status || "--"}
      //       </div>
      //     );
      //   },
      // }),
      columnHelper.accessor("amount", {
        header: () => <div className="th-text">Amount</div>,
        cell: (info) => {
          const amount = info.getValue();
          const displayAmount = amount > 0 ? amount : info.row.original.total_amount;
          return (
            <div className="fw-medium text-sm">
              ${Number(displayAmount ?? 0).toFixed(2)}
            </div>
          );
        },
      }),
      columnHelper.accessor("paid", {
        header: () => <div className="th-text ">Paid</div>,
        cell: (info) => (
            <div className="text-sm" style={{color :"#00a63e"}}>
                ${Number(info.getValue() ?? 0).toFixed(2)}
            </div>
        ),
    }),
    columnHelper.accessor("balance", {
        header: () => <div className="th-text ">Balance</div>,
        cell: (info) => {
            const overPaid = info.row.original.over_paid;
            const balanceValue = Number(info.getValue() ?? 0).toFixed(2);
            return (
                <div className={`text-sm fw-medium`} style={{ color: overPaid ? "#00a63e" : "#e7000b" }}>
                    {overPaid ? `+$${balanceValue}` : `-$${balanceValue}`}
                </div>
            );
        },
    }),

      // columnHelper.accessor("payment_status", {
      //   header: () => <div className="th-text">Payment Status</div>,
      //   cell: (info) => {
      //     const status = info.getValue();
      //     return (
      //       <div className="text-sm">
      //         {status || "--"}
      //       </div>
      //     );
      //   },
      // }),
      // columnHelper.accessor("payment_method", {
      //   header: () => <div className="th-text">Payment Method</div>,
      //   cell: (info) => {
      //     const method = info.getValue();
      //     return (
      //       <div className="text-sm">
      //         {method || "--"}
      //       </div>
      //     );
      //   },
      // }),
      // columnHelper.accessor("notes", {
      //   header: () => <div className="th-text">Notes</div>,
      //   cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
      // }),
    ];
    // eslint-disable-next-line
  }, [pagination.pageIndex, pagination.pageSize]);

  // table instance
  const table = useReactTable({
    data: data || [],
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: { pagination },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
  });

  return (
    <div className="content">
      {/* Header */}
      <div className="mb-4">
        <div>
          <h4 className="mb-1 po-title">Removals Billing & Summary</h4>
          <p className="po-subtitle">Track removal billing and payment status across your organization</p>
        </div>
      </div>

      {/* Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard
            label="Total Removals"
            value={cards?.total_orders ?? 0}
            money={false}
            accent="blue"
            icon="ri-settings-3-line"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard
            label="Total Revenue"
            value={cards?.total_revenue ?? 0}
            money
            accent="green"
            icon="bi-graph-up-arrow"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard
            label="Amount Paid"
            value={cards?.amount_paid ?? 0}
            money
            accent="green"
            icon="bi-currency-dollar"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard
            label="Outstanding Balance"
            value={cards?.outstanding_balance ?? 0}
            money
            accent="red"
            icon="bi-exclamation-octagon"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2" />
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Agent, Brokerage, Office, Team Member ..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleGenerate();
                }}
                autoComplete="off"
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls="removal-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            <button
              type="button"
              className="export-button"
              onClick={handleExportCsv}
              disabled={exporting}
            >
              <i className="ri-file-excel-2-line me-2" />
              {exporting ? "Exporting…" : "Export to Excel"}
            </button>
          </div>
        </div>

        <div id="removal-filters" className={`po-filters ${filtersOpen ? "show" : ""}`} style={{ overflow: "visible" }}>
          <div className="po-filters-bar d-flex" style={{ overflow: "visible" }}>
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto" style={{ overflow: "visible" }}>
              <div className="col-lg-2" style={{ position: "relative" }}>
                <label className="form-label mb-1 label-1">Date Range</label>
                <Select
                  options={DATE_OPTIONS}
                  value={datePreset}
                  onChange={(v) => v && setDatePreset(v)}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                  classNamePrefix="rs"
                />
              </div>

              {datePreset.value === "custom" && (
                <>
                  <div className="col-lg-2">
                    <label className="form-label mb-1 label-1">From</label>
                    <input
                      type="date"
                      className="form-control"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      min={dateRangeLimits.minDate}
                      max={dateRangeLimits.maxDate}
                    />
                  </div>
                  <div className="col-lg-2">
                    <label className="form-label mb-1 label-1">To</label>
                    <input
                      type="date"
                      className="form-control"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                      min={dateRangeLimits.minDate}
                      max={dateRangeLimits.maxDate}
                    />
                  </div>
                </>
              )}

              <div className="col-lg-2" style={{ position: "relative" }}>
                <label className="form-label mb-1 label-1">Payment Status</label>
                <Select
                  options={PAYMENT_STATUS_OPTIONS}
                  value={paymentStatus}
                  onChange={(v) => v && setPaymentStatus(v)}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                  classNamePrefix="rs"
                />
              </div>

              {/* <div className="col-lg-2" style={{ position: "relative" }}>
                <label className="form-label mb-1 label-1">Payment Method</label>
                <Select
                  options={PAYMENT_METHOD_OPTIONS}
                  value={paymentMethod}
                  onChange={(v) => v && setPaymentMethod(v)}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                  classNamePrefix="rs"
                />
              </div> */}

              <div className="col-lg-auto d-flex align-items-end gap-2">
                <button className="btn btn-primary table-addbutton flex-fill flex-md-grow-0" onClick={handleGenerate}>
                  Apply Filter
                </button>
                <button className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-md-grow-0" onClick={handleClearFilters}>
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <div className="d-flex align-items-center">
            <i className="ri-file-text-line me-2" aria-hidden="true" style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }} />
            <h5 className="mb-0">Removal Data</h5>
          </div>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />

         {/* Desktop / Tablet */}
         <div className="rdt-desktop">
           <div className="production-scroll" style={{ "--rdt-min": "1400px" } as React.CSSProperties}>
             <table className="table production-table align-middle mb-0 table-1">
               {loading ? (
                 <DesktopTableLoader table={table} message="Loading removal summary…" />
               ) : (
                 <DesktopTableParts table={table} />
               )}
             </table>
           </div>
         </div>

        {/* Mobile */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading removal summary…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="agent_name"     // pick existing columns for safety
            titleColId="brokerage_firm"
            actionColId="balance"    // shows key financial field
          />
          )}
        </div>
      </div>

      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
    </div>
  );
}